import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDE_ROUTES = ["/admin", "/login"];

function genSessionId() {
  let s = localStorage.getItem("bes_chat_session");
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("bes_chat_session", s);
  }
  return s;
}

export default function ChatWidget() {
  const location = useLocation();
  const hidden = HIDE_ROUTES.some((r) => location.pathname.startsWith(r));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "ATLAS online. Ask about any service, our territory, or how to schedule a site assessment." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(genSessionId());
  const scrollRef = useRef();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  async function send() {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Comms interrupted. Please call (833) 8BESPOKE or submit a quote." }]);
    } finally { setLoading(false); }
  }

  if (hidden) return null;

  return (
    <>
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-btn"
        className="fixed z-50 h-14 w-14 flex items-center justify-center transition-all duration-500"
        style={{
          bottom: 24,
          left: 24,
          background: "rgba(20,20,20,0.72)",
          backdropFilter: "blur(18px) saturate(140%)",
          color: "var(--gold)",
          border: "1px solid var(--line-gold)",
          borderRadius: "50%",
          boxShadow: "0 12px 32px -8px rgba(0,0,0,0.75), 0 0 24px rgba(198,168,124,0.18)",
        }}
        whileHover={{ scale: 1.08, boxShadow: "0 16px 40px -8px rgba(0,0,0,0.85), 0 0 32px rgba(198,168,124,0.35)" }}
        whileTap={{ scale: 0.96 }}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <X size={20} /> : <MessageSquare size={20} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed z-50 w-[min(380px,calc(100vw-3rem))] h-[520px] flex flex-col"
            style={{
              bottom: 96,
              left: 24,
              background: "rgba(12,12,12,0.88)",
              backdropFilter: "blur(28px) saturate(150%)",
              WebkitBackdropFilter: "blur(28px) saturate(150%)",
              border: "1px solid var(--line-gold)",
              boxShadow: "0 44px 80px -20px rgba(0,0,0,0.85), 0 0 40px rgba(198,168,124,0.10)",
            }}
            data-testid="chat-panel"
          >
            <div className="px-5 py-4 flex items-center gap-3" style={{ borderBottom: "1px solid var(--line)" }}>
              <motion.span
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.6, repeat: Infinity }}
                style={{ display: "inline-block", width: 8, height: 8, background: "var(--gold)", borderRadius: 999, boxShadow: "0 0 10px var(--gold)" }}
              />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold)" }}>ATLAS · AI Comms</div>
                <div className="font-mono text-[9px] uppercase tracking-widest mt-0.5" style={{ color: "var(--text-3)" }}>Bespoke Exterior Solutions</div>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-right" : "text-left"} data-testid={`chat-msg-${i}`}>
                  <div
                    className="inline-block max-w-[85%] px-3.5 py-2.5 text-sm leading-snug"
                    style={{
                      background: m.role === "user" ? "var(--gold)" : "rgba(255,255,255,0.04)",
                      color: m.role === "user" ? "#111" : "var(--text)",
                      border: `1px solid ${m.role === "user" ? "var(--gold)" : "var(--line)"}`,
                    }}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="text-left">
                  <div className="inline-block px-3.5 py-2.5 text-sm font-mono" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid var(--line)", color: "var(--text-3)" }}>
                    ...transmitting
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 flex items-center gap-2" style={{ borderTop: "1px solid var(--line)" }}>
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about a service…"
                className="field-boxed flex-1"
                style={{ padding: "0.65rem 0.85rem", fontSize: "0.9rem" }}
              />
              <button
                data-testid="chat-send-btn"
                onClick={send}
                disabled={loading}
                className="h-11 w-11 flex items-center justify-center transition-all duration-300 disabled:opacity-60"
                style={{ background: "var(--gold)", color: "#111", border: "1px solid var(--gold)" }}
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
